import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, Hash, Zap, CheckCircle2 } from 'lucide-react';

interface CheckStats {
  numbersChecked: number;
  primesFound: number;
  totalTime: number;
  averageTime: number;
  largestPrimeChecked: number;
}

interface PrimeResult {
  number: number;
  isPrime: boolean;
  factors: number[];
  algorithm: string;
  processingTime: number;
  explanation: string;
}

const PrimeNumberCheckerTool: React.FC = () => {
  const [checkType, setCheckType] = useState<string>('single');
  const [inputNumber, setInputNumber] = useState<string>('');
  const [rangeStart, setRangeStart] = useState<string>('');
  const [rangeEnd, setRangeEnd] = useState<string>('');
  const [generateCount, setGenerateCount] = useState<string>('10');
  const [result, setResult] = useState<PrimeResult | null>(null);
  const [rangeResults, setRangeResults] = useState<number[]>([]);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [stats, setStats] = useState<CheckStats>({
    numbersChecked: 0,
    primesFound: 0,
    totalTime: 0,
    averageTime: 0,
    largestPrimeChecked: 0
  });
  const [notification, setNotification] = useState<string>('');
  const [history, setHistory] = useState<PrimeResult[]>([]);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // Prime checking algorithms
  const isPrimeTrial = (n: number): { isPrime: boolean; factors: number[] } => {
    if (n < 2) return { isPrime: false, factors: [] };
    if (n === 2) return { isPrime: true, factors: [] };
    if (n % 2 === 0) return { isPrime: false, factors: [2, n / 2] };

    const factors: number[] = [];
    const sqrt = Math.sqrt(n);
    
    for (let i = 3; i <= sqrt; i += 2) {
      if (n % i === 0) {
        factors.push(i, n / i);
        return { isPrime: false, factors };
      }
    }
    
    return { isPrime: true, factors: [] };
  };

  const isPrimeOptimized = (n: number): { isPrime: boolean; factors: number[] } => {
    if (n < 2) return { isPrime: false, factors: [] };
    if (n === 2 || n === 3) return { isPrime: true, factors: [] };
    if (n % 2 === 0) return { isPrime: false, factors: [2, n / 2] };
    if (n % 3 === 0) return { isPrime: false, factors: [3, n / 3] };

    const factors: number[] = [];
    const sqrt = Math.sqrt(n);
    
    // Check for factors of the form 6k ± 1
    for (let i = 5; i <= sqrt; i += 6) {
      if (n % i === 0) {
        factors.push(i, n / i);
        return { isPrime: false, factors };
      }
      if (n % (i + 2) === 0) {
        factors.push(i + 2, n / (i + 2));
        return { isPrime: false, factors };
      }
    }
    
    return { isPrime: true, factors: [] };
  };

  const getAllFactors = (n: number): number[] => {
    const factors: number[] = [];
    const sqrt = Math.sqrt(n);
    
    for (let i = 1; i <= sqrt; i++) {
      if (n % i === 0) {
        factors.push(i);
        if (i !== n / i) {
          factors.push(n / i);
        }
      }
    }
    
    return factors.sort((a, b) => a - b);
  };

  const checkPrimeNumber = useCallback((num: number): PrimeResult | null => {
    if (isNaN(num) || num < 0 || !Number.isInteger(num)) return null;

    const startTime = performance.now();
    const algorithm = num > 1000000 ? 'optimized' : 'trial-division';
    
    let primeCheck: { isPrime: boolean; factors: number[] };
    if (algorithm === 'optimized') {
      primeCheck = isPrimeOptimized(num);
    } else {
      primeCheck = isPrimeTrial(num);
    }

    const endTime = performance.now();
    const processingTime = endTime - startTime;

    let explanation = '';
    if (num < 2) {
      explanation = `${num} is not prime because prime numbers must be greater than 1.`;
    } else if (num === 2) {
      explanation = `${num} is prime. It's the only even prime number.`;
    } else if (primeCheck.isPrime) {
      explanation = `${num} is prime! It has no divisors other than 1 and itself.`;
    } else {
      const factorList = getAllFactors(num);
      explanation = `${num} is not prime. It can be divided by: ${factorList.join(', ')}. The smallest prime factors are: ${primeCheck.factors.join(' × ')} = ${num}.`;
    }

    return {
      number: num,
      isPrime: primeCheck.isPrime,
      factors: getAllFactors(num),
      algorithm,
      processingTime,
      explanation
    };
  }, []);

  const generatePrimesInRange = (start: number, end: number): number[] => {
    const primes: number[] = [];
    for (let i = Math.max(2, start); i <= end; i++) {
      const check = isPrimeOptimized(i);
      if (check.isPrime) {
        primes.push(i);
      }
    }
    return primes;
  };

  const generateFirstNPrimes = (n: number): number[] => {
    const primes: number[] = [];
    let candidate = 2;
    
    while (primes.length < n) {
      const check = isPrimeOptimized(candidate);
      if (check.isPrime) {
        primes.push(candidate);
      }
      candidate++;
    }
    
    return primes;
  };

  const performCheck = useCallback(async (): Promise<void> => {
    setIsProcessing(true);
    
    try {
      if (checkType === 'single') {
        const num = parseInt(inputNumber);
        const checkResult = checkPrimeNumber(num);
        if (checkResult) {
          setResult(checkResult);
          setRangeResults([]);
          setHistory(prev => [checkResult, ...prev.slice(0, 9)]);
          setStats(prev => ({
            numbersChecked: prev.numbersChecked + 1,
            primesFound: prev.primesFound + (checkResult.isPrime ? 1 : 0),
            totalTime: prev.totalTime + checkResult.processingTime,
            averageTime: (prev.totalTime + checkResult.processingTime) / (prev.numbersChecked + 1),
            largestPrimeChecked: checkResult.isPrime && checkResult.number > prev.largestPrimeChecked 
              ? checkResult.number 
              : prev.largestPrimeChecked
          }));
        }
      } else if (checkType === 'range') {
        const start = parseInt(rangeStart);
        const end = parseInt(rangeEnd);
        if (!isNaN(start) && !isNaN(end) && start <= end) {
          const startTime = performance.now();
          const primes = generatePrimesInRange(start, end);
          const endTime = performance.now();
          
          setRangeResults(primes);
          setResult({
            number: primes.length,
            isPrime: true,
            factors: [],
            algorithm: 'range-generation',
            processingTime: endTime - startTime,
            explanation: `Found ${primes.length} prime numbers between ${start} and ${end}.`
          });
          
          setStats(prev => ({
            numbersChecked: prev.numbersChecked + (end - start + 1),
            primesFound: prev.primesFound + primes.length,
            totalTime: prev.totalTime + (endTime - startTime),
            averageTime: (prev.totalTime + (endTime - startTime)) / (prev.numbersChecked + (end - start + 1)),
            largestPrimeChecked: primes.length > 0 && primes[primes.length - 1] > prev.largestPrimeChecked 
              ? primes[primes.length - 1] 
              : prev.largestPrimeChecked
          }));
        }
      } else if (checkType === 'generate') {
        const count = parseInt(generateCount);
        if (!isNaN(count) && count > 0) {
          const startTime = performance.now();
          const primes = generateFirstNPrimes(count);
          const endTime = performance.now();
          
          setRangeResults(primes);
          setResult({
            number: count,
            isPrime: true,
            factors: [],
            algorithm: 'prime-generation',
            processingTime: endTime - startTime,
            explanation: `Generated the first ${count} prime numbers. The largest is ${primes[primes.length - 1]}.`
          });
          
          setStats(prev => ({
            numbersChecked: prev.numbersChecked + count,
            primesFound: prev.primesFound + count,
            totalTime: prev.totalTime + (endTime - startTime),
            averageTime: (prev.totalTime + (endTime - startTime)) / (prev.numbersChecked + count),
            largestPrimeChecked: primes[primes.length - 1] > prev.largestPrimeChecked 
              ? primes[primes.length - 1] 
              : prev.largestPrimeChecked
          }));
        }
      }
    } finally {
      setIsProcessing(false);
    }
  }, [checkType, inputNumber, rangeStart, rangeEnd, generateCount, checkPrimeNumber]);

  useEffect(() => {
    if (checkType === 'single' && inputNumber) {
      const timeoutId = setTimeout(performCheck, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [inputNumber, checkType, performCheck]);

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (): Promise<void> => {
    if (!result) return;
    
    let textToCopy = '';
    if (checkType === 'single') {
      textToCopy = `Number: ${result.number}\nIs Prime: ${result.isPrime ? 'Yes' : 'No'}\n${result.explanation}`;
      if (!result.isPrime && result.factors.length > 0) {
        textToCopy += `\nFactors: ${result.factors.join(', ')}`;
      }
    } else {
      textToCopy = `${result.explanation}\nPrimes: ${rangeResults.join(', ')}`;
    }
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      showNotification('Results copied to clipboard!');
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadReport = (): void => {
    if (!result) return;
    
    let reportContent = `Prime Number Analysis Report
Generated: ${new Date().toLocaleString()}

`;

    if (checkType === 'single') {
      reportContent += `Single Number Check:
Number: ${result.number}
Is Prime: ${result.isPrime ? 'Yes' : 'No'}
Algorithm: ${result.algorithm}
Processing Time: ${result.processingTime.toFixed(2)}ms
Explanation: ${result.explanation}

`;
      if (!result.isPrime && result.factors.length > 0) {
        reportContent += `All Factors: ${result.factors.join(', ')}\n\n`;
      }
    } else {
      reportContent += `${checkType === 'range' ? 'Range Analysis' : 'Prime Generation'}:
${result.explanation}
Processing Time: ${result.processingTime.toFixed(2)}ms

Prime Numbers Found:
${rangeResults.join(', ')}

`;
    }

    reportContent += `Recent Checks:
${history.slice(0, 5).map((check, index) => 
  `${index + 1}. ${check.number} - ${check.isPrime ? 'Prime' : 'Not Prime'}`
).join('\n')}

Statistics:
- Total Numbers Checked: ${stats.numbersChecked}
- Primes Found: ${stats.primesFound}
- Average Processing Time: ${stats.averageTime.toFixed(2)}ms
- Largest Prime Checked: ${stats.largestPrimeChecked}
`;

    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'prime-number-analysis-report.txt';
    a.click();
    URL.revokeObjectURL(url);
    showNotification('Report downloaded!');
  };

  const clearAll = (): void => {
    setInputNumber('');
    setRangeStart('');
    setRangeEnd('');
    setGenerateCount('10');
    setResult(null);
    setRangeResults([]);
    setHistory([]);
    setStats({
      numbersChecked: 0,
      primesFound: 0,
      totalTime: 0,
      averageTime: 0,
      largestPrimeChecked: 0
    });
    showNotification('Cleared all data!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSampleCheck = (): void => {
    setCheckType('single');
    setInputNumber('97');
    showNotification('Sample number loaded!');
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-purple-500 to-blue-600 rounded-lg">
              <Hash className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Prime Number Checker</h1>
              <p className="text-gray-300 text-sm">Advanced prime number analysis and generation tool</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Check Type Selector */}
        <div className="mb-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Check Type</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {[
              { value: 'single', label: 'Single Number Check', desc: 'Check if a specific number is prime' },
              { value: 'range', label: 'Range Analysis', desc: 'Find all primes in a range' },
              { value: 'generate', label: 'Generate Primes', desc: 'Generate first N prime numbers' }
            ].map((type) => (
              <button
                key={type.value}
                onClick={() => setCheckType(type.value)}
                className={`p-3 rounded-lg text-left transition-colors ${
                  checkType === type.value
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <div className="font-medium">{type.label}</div>
                <div className="text-xs opacity-80">{type.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Analysis Statistics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-purple-400">{stats.numbersChecked}</div>
                <div className="text-gray-300 text-sm">Numbers Checked</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-blue-400">{stats.primesFound}</div>
                <div className="text-gray-300 text-sm">Primes Found</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats.averageTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Avg Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{stats.largestPrimeChecked}</div>
                <div className="text-gray-300 text-sm">Largest Prime</div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[600px]">
          {/* Input Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Input</h2>
              <div className="flex space-x-2">
                <button
                  onClick={loadSampleCheck}
                  className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Zap className="w-4 h-4" />
                </button>
                <button
                  onClick={clearAll}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="space-y-4 flex-1">
              {checkType === 'single' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Number to Check
                  </label>
                  <input
                    type="number"
                    value={inputNumber}
                    onChange={(e) => setInputNumber(e.target.value)}
                    placeholder="Enter a number to check"
                    className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none text-lg"
                  />
                </div>
              )}
              
              {checkType === 'range' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Start Number
                    </label>
                    <input
                      type="number"
                      value={rangeStart}
                      onChange={(e) => setRangeStart(e.target.value)}
                      placeholder="Enter start number"
                      className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none text-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      End Number
                    </label>
                    <input
                      type="number"
                      value={rangeEnd}
                      onChange={(e) => setRangeEnd(e.target.value)}
                      placeholder="Enter end number"
                      className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none text-lg"
                    />
                  </div>
                  <button
                    onClick={performCheck}
                    disabled={isProcessing || !rangeStart || !rangeEnd}
                    className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white p-3 rounded-lg transition-colors"
                  >
                    {isProcessing ? 'Finding Primes...' : 'Find Primes in Range'}
                  </button>
                </>
              )}
              
              {checkType === 'generate' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Number of Primes to Generate
                    </label>
                    <input
                      type="number"
                      value={generateCount}
                      onChange={(e) => setGenerateCount(e.target.value)}
                      placeholder="Enter count"
                      className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none text-lg"
                    />
                  </div>
                  <button
                    onClick={performCheck}
                    disabled={isProcessing || !generateCount}
                    className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white p-3 rounded-lg transition-colors"
                  >
                    {isProcessing ? 'Generating...' : 'Generate Prime Numbers'}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Output Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Result</h2>
              <div className="flex space-x-2">
                <button
                  onClick={copyToClipboard}
                  disabled={!result}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={downloadReport}
                  disabled={!result}
                  className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 bg-gray-900 rounded-lg p-4 overflow-y-auto min-h-[400px]">
              {result ? (
                <div className="space-y-4">
                  {checkType === 'single' ? (
                    <>
                      <div className="text-center">
                        <div className={`text-4xl font-bold mb-2 ${result.isPrime ? 'text-green-400' : 'text-red-400'}`}>
                          {result.isPrime ? '✓ PRIME' : '✗ NOT PRIME'}
                        </div>
                        <div className="text-2xl text-white mb-2">{result.number}</div>
                        <div className="text-gray-400 text-sm">
                          Checked in {result.processingTime.toFixed(2)}ms using {result.algorithm}
                        </div>
                      </div>
                      
                      <div className="border-t border-gray-600 pt-4">
                        <h4 className="text-white font-medium mb-2">Explanation:</h4>
                        <div className="text-gray-300 text-sm leading-relaxed">
                          {result.explanation}
                        </div>
                      </div>
                      
                      {!result.isPrime && result.factors.length > 0 && (
                        <div>
                          <h4 className="text-white font-medium mb-2">All Factors:</h4>
                          <div className="bg-gray-700 p-3 rounded">
                            <div className="text-yellow-400 font-mono text-sm">
                              {result.factors.join(', ')}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="text-center">
                        <div className="text-4xl font-bold text-green-400 mb-2">
                          {rangeResults.length}
                        </div>
                        <div className="text-white mb-2">Prime Numbers Found</div>
                        <div className="text-gray-400 text-sm">
                          Generated in {result.processingTime.toFixed(2)}ms
                        </div>
                      </div>
                      
                      <div className="border-t border-gray-600 pt-4">
                        <h4 className="text-white font-medium mb-2">Explanation:</h4>
                        <div className="text-gray-300 text-sm leading-relaxed">
                          {result.explanation}
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="text-white font-medium mb-2">Prime Numbers:</h4>
                        <div className="bg-gray-700 p-3 rounded max-h-40 overflow-y-auto">
                          <div className="text-green-400 font-mono text-sm">
                            {rangeResults.join(', ')}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <Hash className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Enter a number or range to check for primes</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* History Section */}
        {history.length > 0 && (
          <div className="mt-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Recent Checks</h3>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {history.slice(0, 5).map((check, index) => (
                <div key={index} className="bg-gray-700 p-2 rounded text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-mono">{check.number}</span>
                    <span className={`font-medium ${check.isPrime ? 'text-green-400' : 'text-red-400'}`}>
                      {check.isPrime ? 'Prime' : 'Not Prime'}
                    </span>
                  </div>
                  <div className="text-gray-400 text-xs">{check.processingTime.toFixed(2)}ms</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrimeNumberCheckerTool;